/**
 * Created by Ella Ma on 04/24/2019.
 * Description:
 *
 * ------ maintenance history ------
 * 01/14/2020 Ella Ma - Add 'Don't include blank item as category' when tile type is Pie/Column/Bar
 */

import { Component, OnInit, Input } from '@angular/core';
import { TamDashboardTileType, PieChartDisplaySetting } from '../../../tam-dashboard/tam-dashboard.model';
import * as _lodash from 'lodash';
import { TileModel } from '../../../../tamalelibs/models/tile.model';
import { DashboardService } from '../../../../pages/dashboard/dashboard.service';
import { Validatable } from '../tile-choose-visualization/tile-choose-visualization.model';
import { businessConstants } from '../../../../constants/business.constants';

@Component({
    selector: 'tam-tile-chart-setting',
    templateUrl: './tile-chart-setting.component.html',
    styleUrls: ['./tile-chart-setting.component.scss']
})
export class TileChartSettingComponent implements OnInit, Validatable {

    @Input() tile: TileModel;

    visualizationType = TamDashboardTileType;
    // categoryAxis series categorySort categoryCount groupBy column summaries
    data: any = {};
    summaryDisabled = false;
    submitted = false;
    categorySource: Array<any> = [];
    categorySearchKey = '';
    numberAndCountFields: Array<any>;
    valueSearchKey = '';
    categorySortOptions = [
        { id: '', name: '' },
        { id: 'desc', name: 'Descending' },
        { id: 'asc', name: 'Ascending' }
    ];
    displaySettingOptions = [
        { id: PieChartDisplaySetting.PERCENTAGE, name: 'Percentage' },
        { id: PieChartDisplaySetting.VALUE, name: 'Value' },
    ];
    dateFields: Array<any>;
    xAxisSearchKey = '';
    seriesSearchKey = '';
    groupBySource: Array<any>;
    groupBySearchKey = '';
    columnSource: Array<any>;
    columnSearchKey = '';
    summaryOptions = [
        { id: '0', name: '' },
        { id: '1', name: 'Average' },
        { id: '2', name: 'Maximum' },
        { id: '3', name: 'Minimum' },
        { id: '4', name: 'Sum' },
        { id: '5', name: 'Count' }
    ];
    enableBlankCategory = false;
    enableUnnestColumn = false;
    unusableUnnestColumn = false;

    constructor(
        private _dashboardService: DashboardService,
    ) { }

    ngOnInit() {
        this.submitted = false;
        this.columnSource = [];
        this.dateFields = [];
        this.categorySource = [];
        this.groupBySource = [];
        this.numberAndCountFields = [{ title: 'Count', guid: 'efb04d645da94dacbbcd568a83b5f12p' }];
        if (this.tile.columns && this.tile.columns.length > 0) {
            this.tile.columns.forEach((item) => {
                // providerType 0-rich-client mdl 1-web mdl 2-sql mdl 3-oracle mdl

                // Pie Chart, Column Chart, Bar Chart (Related to MDL)
                // User should be able to select date and text from MDL as category.
                // User should be able to select number from mdl as value

                // Line Chart (Related to MDL)
                // User should be able to select date from MDL as X-Axis
                // User should be able to select number from MDL as value
                // User should be able to select text from MDL as category(group by)
                const isNumber = item.dataType === 'ratio' || item.dataType === 'percent' ||
                    item.dataType === 'currency' || item.dataType === 'number';
                const dateType = this._dashboardService.isDateType(item.dataType);
                const isSupportMdl = item.columnType === 'MDL' &&
                    item.config.providerType > 0;

                const isCategorySupportMdl = isSupportMdl && (dateType || item.dataType === 'text');
                const isValueSupportMdl = isSupportMdl && isNumber;
                const isXAxisSupportMdl = isSupportMdl && dateType;
                const isGroupBySupportMdl = isSupportMdl && item.dataType === 'text';

                // value tile column
                if (!item.hidden) {
                    this.columnSource.push(item);
                }
                if (item.columnType !== 'Formula') {
                    // pie/column group by - category;
                    if (item.columnType !== 'MDL' &&
                        (item.dataType === 'entity' || dateType || item.dataType === 'text') || isCategorySupportMdl) {
                        this.categorySource.push(item);
                    }
                    // pie/column value && line chart series;
                    if ((item.columnType !== 'MDL' &&
                        item.dataType !== 'entity' && !dateType && item.dataType !== 'text') || isValueSupportMdl) {
                        this.numberAndCountFields.push(item);
                    }
                    // line chart X-Axis
                    if ((item.columnType !== 'MDL' && dateType) || isXAxisSupportMdl) {
                        this.dateFields.push(item);
                    }
                    // line chart group by
                    if ((item.columnType !== 'MDL' && (item.dataType === 'text' || item.dataType === 'entity')) || isGroupBySupportMdl) {
                        this.groupBySource.push(item);
                    }
                }
            });
        }
        this.initData();
    }

    initData() {
        if (this.tile.visualization.type === this.visualizationType.PIE_CHART) {
            const displaySetting = this.displaySettingOptions.filter(item => item.id === this.tile.visualization.settings.displaySetting);
            if (displaySetting && displaySetting.length > 0) {
                this.data['displaySetting'] = displaySetting[0];
            } else {
                this.data['displaySetting'] = this.displaySettingOptions[0];
            }
            this.tile.visualization.settings['displaySetting'] = this.data['displaySetting'].id;

            this.enableUnnestColumn = !!this.tile.visualization.settings.enableUnnestColumn;
            const categoryAxis = this.categorySource.filter(item => item.guid === this.tile.visualization.settings.categoryAxis);
            if (categoryAxis && categoryAxis.length > 0) {
                this.data['categoryAxis'] = categoryAxis[0];

                if (this.data['categoryAxis'].dataType === 'entity' && this.data['categoryAxis'].columnType === 'entryColumn') {
                    this.unusableUnnestColumn = true;
                } else {
                    this.unusableUnnestColumn = false;
                    this.enableUnnestColumn = false;
                }
            }
        }
        if (this.tile.visualization.type === this.visualizationType.PIE_CHART ||
            this.tile.visualization.type === this.visualizationType.COLUMN_CHART ||
            this.tile.visualization.type === this.visualizationType.BAR_CHART) {
            this.enableBlankCategory = !!this.tile.visualization.settings.enableBlankCategory;
            const categoryAxis = this.categorySource.filter(item => item.guid === this.tile.visualization.settings.categoryAxis);
            if (categoryAxis && categoryAxis.length > 0) {
                this.data['categoryAxis'] = categoryAxis[0];
            }
            const series = this.numberAndCountFields.filter(item => item.guid === this.tile.visualization.settings.series);
            if (series && series.length > 0) {
                this.data['series'] = series[0];
            }
        }
        if (this.tile.visualization.type === this.visualizationType.COLUMN_CHART ||
            this.tile.visualization.type === this.visualizationType.BAR_CHART) {
            const categorySort = this.categorySortOptions.filter(item => item.id === this.tile.visualization.settings.categorySort);
            if (categorySort && categorySort.length > 0) {
                this.data['categorySort'] = categorySort[0];
            }
            this.data['categoryCount'] = this.tile.visualization.settings.categoryCount;
        } else if (this.tile.visualization.type === this.visualizationType.LINE_CHART) {
            const categoryAxis = this.dateFields.filter(item => item.guid === this.tile.visualization.settings.categoryAxis);
            if (categoryAxis && categoryAxis.length > 0) {
                this.data['categoryAxis'] = categoryAxis[0];
            }
            const series = this.numberAndCountFields.filter(item => item.guid === this.tile.visualization.settings.series);
            if (series && series.length > 0) {
                this.data['series'] = series[0];
            }
            const groupBy = this.groupBySource.filter(item => item.guid === this.tile.visualization.settings.groupBy);
            if (groupBy && groupBy.length > 0) {
                this.data['groupBy'] = groupBy[0];
            }
        } else if (this.tile.visualization.type === this.visualizationType.VALUE) {
            const column = this.columnSource.filter(item => item.guid === this.tile.visualization.settings.column);
            if (column && column.length > 0) {
                this.data['column'] = column[0];
            }
            this.updateSummaries(this.tile.visualization.settings.summaries);
            const summaries = this.summaryOptions.filter(item => item.id === this.tile.visualization.settings.summaries);
            if (summaries && summaries.length > 0) {
                this.data['summaries'] = summaries[0];
            }
        }
    }

    valueChange(type) {
        this.submitted = false;
        if (type === 'categorySort' || type === 'summaries' || type === 'displaySetting') {
            this.tile.visualization.settings[type] = this.data[type] ? this.data[type].id : '';
        } else if (type === 'categoryCount') {
            this.tile.visualization.settings[type] = this.data[type];
        } else {
            if (type === 'categoryAxis') {
                if (this.data[type] && this.data[type].dataType === 'entity' && this.data[type].columnType === 'entryColumn') {
                    this.unusableUnnestColumn = true;
                    this.enableUnnestColumn = false;
                } else {
                    this.unusableUnnestColumn = false;
                    this.enableUnnestColumn = false;
                }
                this.tile.visualization.settings.enableUnnestColumn = this.enableUnnestColumn;
            }
            this.tile.visualization.settings[type] = this.data[type] ? this.data[type].guid : '';
        }
    }

    categoryData() {
        return this.categorySource.filter(item => item.title.toLowerCase().indexOf(this.categorySearchKey.toLowerCase()) > -1 && item.field !== businessConstants.dashboard.field.entry_file);
    }

    handleFilterCategory(value) {
        this.categorySearchKey = value;
    }

    changeEnableBlankCategory() {
        this.tile.visualization.settings.enableBlankCategory = this.enableBlankCategory;
    }

    changeEnableUnnestColumn() {
        this.tile.visualization.settings.enableUnnestColumn = this.enableUnnestColumn;
    }

    valueData() {
        return this.numberAndCountFields.filter(item => item.title.toLowerCase().indexOf(this.valueSearchKey.toLowerCase()) > -1);
    }

    handleFilterValue(value) {
        this.valueSearchKey = value;
    }

    xAxisData() {
        return this.dateFields.filter(item => item.title.toLowerCase().indexOf(this.xAxisSearchKey.toLowerCase()) > -1);
    }

    handleFilterXAxis(value) {
        this.xAxisSearchKey = value;
    }

    seriesData() {
        return this.numberAndCountFields.filter(item => item.title.toLowerCase().indexOf(this.seriesSearchKey.toLowerCase()) > -1);
    }

    handleFilterSeries(value) {
        this.seriesSearchKey = value;
    }

    groupByData() {
        return this.groupBySource.filter(item => item.title.toLowerCase().indexOf(this.groupBySearchKey.toLowerCase()) > -1);
    }

    handleFilterGroupBy(value) {
        this.groupBySearchKey = value;
    }

    columnData() {
        return this.columnSource.filter(item => item.title && item.title.toLowerCase().indexOf(this.columnSearchKey.toLowerCase()) > -1);
    }

    handleFilterColumn(value) {
        this.columnSearchKey = value;
    }

    updateSummaries(selSummaries) {
        if (!this.data || !this.data.column) {
            return;
        }
        this.valueChange('column');
        for (let i = 0; i < this.columnSource.length; i++) {
            if (this.data.column.guid === this.columnSource[i].guid) {
                if (this.columnSource[i].columnType === 'Formula') {
                    this.summaryOptions = [
                        { id: '0', name: '' }
                    ];
                    this.data.summaries = this.summaryOptions[0];
                    this.tile.visualization.settings['summaries'] = this.data.summaries.id;
                    this.summaryDisabled = true;
                } else if (this.columnSource[i].dataType === 'number' ||
                    this.columnSource[i].dataType === 'percent' ||
                    this.columnSource[i].dataType === 'ratio' ||
                    this.columnSource[i].dataType === 'currency') {
                    this.summaryOptions = [
                        { id: '0', name: '' },
                        { id: '1', name: 'Average' },
                        { id: '2', name: 'Maximum' },
                        { id: '3', name: 'Minimum' },
                        { id: '4', name: 'Sum' },
                        { id: '5', name: 'Count' }
                    ];
                    if (selSummaries) {
                        const summaries = this.summaryOptions.filter(item => item.id === this.tile.visualization.settings.summaries);
                        this.data.summaries = summaries[0];
                    } else {
                        this.data.summaries = this.summaryOptions[0];
                    }
                    this.tile.visualization.settings['summaries'] = this.data.summaries.id;
                    this.summaryDisabled = false;
                } else {
                    this.summaryOptions = [
                        { id: '0', name: '' },
                        { id: '5', name: 'Count' }
                    ];
                    if (this.data.summaries && this.data.summaries.id !== '0' && this.data.summaries.id !== '5') {
                        this.data.summaries = this.summaryOptions[0];
                        this.tile.visualization.settings['summaries'] = this.data.summaries.id;
                    }
                    this.summaryDisabled = false;
                }
                break;
            }
        }
    }

    validate() {
        const settings = this.data;
        if (this.tile.visualization.type === this.visualizationType.PIE_CHART) {
            if (!settings.categoryAxis || !settings.series || !settings.displaySetting) {
                this.submitted = true;
                return false;
            }
        } else if (this.tile.visualization.type === this.visualizationType.COLUMN_CHART ||
            this.tile.visualization.type === this.visualizationType.BAR_CHART) {
            if (!this._barChartLogicValidate()) {
                this.submitted = true;
                return false;
            }
            if (!settings.categoryAxis || !settings.series) {
                this.submitted = true;
                return false;
            }
        } else if (this.tile.visualization.type === this.visualizationType.LINE_CHART) {
            if (!settings.categoryAxis || !settings.series || !settings.groupBy) {
                this.submitted = true;
                return false;
            }
        } else if (this.tile.visualization.type === this.visualizationType.VALUE) {
            if (!settings.column) {
                this.submitted = true;
                return false;
            }
        }
        return true;
    }

    private _barChartLogicValidate() {
        const settings = this.data;
        if (settings.categorySort && settings.categorySort.id === '') {
            delete settings.categorySort;
        }
        if (!settings.categoryCount && !settings.categorySort) {
            return true;
        }
        if (settings.categoryCount && !settings.categorySort) {
            return false;
        }
        if (settings.categorySort && !settings.categoryCount) {
            return false;
        }
        if (settings.categoryCount > 50 || settings.categoryCount <= 0) {
            return false;
        }
        return true;
    }
}
